import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ADD_TO_CART_TOAST_DURATION } from "../lib/toastConfig";
import { useCart } from "../contexts/CartContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { products } from "../data/productData";
import ScrollingTicker from "../components/common/ScrollingTicker";
import ProductCard from "../components/product/ProductCard";
import ProductPrice from "../components/product/ProductPrice";
import Button from "../components/common/Button";
import Seo from "../components/seo/Seo";
import { categoryList } from "../lib/catalog";
import { webpVariant } from "../lib/optimizedImage";
import { formatPrice, getProductPricing } from "../utils/productPricing";
import { getProductDisplayName } from "../utils/productDisplay";
import { isProductVisible } from "../utils/productFilters";
import { getProductPath } from "../utils/productLinks";

// Default preview shown on first render — the on-skin lifestyle shot of our
// featured Best Seller (Glowing Turmeric Body Butter Cream, product id 2).
const FEATURED_PRODUCT_ID = 2;
const buildDefaultPreview = (productList) => {
  const featured = productList.find((p) => p.id === FEATURED_PRODUCT_ID);
  if (!featured) return null;
  const lifestyleImage =
    (Array.isArray(featured.images) && featured.images[2]) ||
    (Array.isArray(featured.images) && featured.images[0]) ||
    featured.image;
  return {
    image: lifestyleImage,
    mediaType: "image",
    activePreviewIndex: (Array.isArray(featured.images)
      ? featured.images
      : [featured.image]
    ).findIndex((image) => image === lifestyleImage),
    caption: {
      title: "On real skin",
      body: "Shown in use — see how it sits on real, glowing skin.",
    },
    product: featured,
    galleryImages: (Array.isArray(featured.images)
      ? featured.images
      : [featured.image]
    )
      .filter(Boolean)
      .map((image) => ({
        image,
        mediaType: /\.(mp4|mov|webm|m4v)$/i.test(image) ? "video" : "image",
        poster: featured.image,
      })),
  };
};

const getHomepageProductKey = (product) =>
  `${product.id}:${product.name}:${product.mainCategories}`;

// Single global medical row below the 2×2 homepage card grid — Scrubs, Pads, Gloves+Mask.
const buildMedicalGallery = (product) => {
  const images = (
    Array.isArray(product?.images) && product.images.length > 0
      ? product.images
      : [product?.image]
  ).filter(Boolean);
  return images.map((src) => ({ src, type: "image" }));
};

const MEDICAL_CELL_META = {
  3: {
    price: "$35",
    body: "Professional scrubs with moisture-wicking stretch fabric for long shifts.",
  },
  21: {
    price: "$35",
    body: '36" × 36" leak-proof disposable pads, 50 per pack.',
  },
  20: {
    price: "$8",
    body: "Powder-free, textured nitrile gloves. 100 pcs per box.",
  },
  19: {
    price: "$8",
    body: "Breathable fit with adjustable nosepiece and soft ear-loops.",
  },
};

const GLOBAL_MEDICAL_GROUP_KEYS = {
  3: "everyday-scrubs-global",
  21: "disposable-pads-global",
  20: "nitrile-gloves-global",
  19: "face-mask-global",
};

const buildMedicalRowCell = (productId) => {
  const product = products.find((entry) => entry.id === productId);
  return {
    ...MEDICAL_CELL_META[productId],
    title: getProductDisplayName(product),
    productId,
    src: product.image,
    groupKey: GLOBAL_MEDICAL_GROUP_KEYS[productId],
    gallery: buildMedicalGallery(product),
  };
};

const globalMedicalRowCells = [
  buildMedicalRowCell(3),
  buildMedicalRowCell(21),
  {
    combined: true,
    title: "Gloves & Face Mask",
    price: "$8 each",
    body: "Powder-free nitrile gloves and breathable disposable face masks.",
    halves: [buildMedicalRowCell(20), buildMedicalRowCell(19)],
  },
];

const FREE_GIFT_PRODUCT_ID = 17;
const FREE_GIFT_ORDER_MESSAGE = "Free on orders above $50";

const buildFreeGiftRowCell = () => {
  const product = products.find((entry) => entry.id === FREE_GIFT_PRODUCT_ID);
  return {
    title: getProductDisplayName(product) || "Kojic Turmeric Scrub",
    productId: FREE_GIFT_PRODUCT_ID,
    src: product?.image ?? "/img/lemonKojic/lemonkojic.png",
    groupKey: "free-gift-lemon-kojic",
    gallery: buildMedicalGallery(product),
    message: FREE_GIFT_ORDER_MESSAGE,
    body: "Receive this exfoliating scrub free when your order total is over $50.",
  };
};

const globalFreeGiftRowCells = [0, 1, 2].map(() => buildFreeGiftRowCell());

const cellToThumbnailOverride = (cell) =>
  cell.empty
    ? { empty: true }
    : {
        src: cell.src,
        productId: cell.productId,
        groupKey: cell.groupKey,
        gallery: cell.gallery,
        poster: cell.gallery?.find((item) => item.type === "video")?.poster,
      };

const cellToCaptionOverride = (cell) =>
  cell.empty ? {} : { title: cell.title, price: cell.price, body: cell.body };

const isMedicalProductOnSale = (productId) =>
  products.find((entry) => entry.id === productId)?.isSale === true;

const isMedicalCellOnSale = (cell) =>
  cell.combined
    ? cell.halves.some((half) => isMedicalProductOnSale(half.productId))
    : isMedicalProductOnSale(cell.productId);

// Matches ProductCard center flashing SALES chip (animate-flash + star icon).
const FlashSalesBadge = () => (
  <span className="animate-flash inline-flex items-center gap-0.5 bg-gradient-to-r from-red-600 via-rose-500 to-red-600 text-white text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-lg ring-2 ring-red-300/70">
    <svg
      className="w-2.5 h-2.5 -ml-0.5"
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M12 2.5l1.7 5.2H19l-4.3 3.1 1.7 5.2L12 12.9 7.6 16l1.7-5.2L5 7.7h5.3z" />
    </svg>
    Sales
  </span>
);

// Same animate-flash pattern as FlashSalesBadge — yellow gradient for Free Gift strip header.
const FlashFreeGiftBadge = () => (
  <span className="animate-flash inline-flex items-center gap-0.5 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 text-black text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-lg ring-2 ring-yellow-300/70">
    Free Gift
  </span>
);

const luminousBodyOilSetGallery = [
  { src: "/img/luminous_set/luminous_set_collage.jpg", type: "image" },
  { src: "/img/whatsappImages/LuminousBOil/LuminousBOil1.jpeg", type: "image" },
  { src: "/img/bee_honey_soap/all_natural_bee_honey_2.JPG", type: "image" },
  { src: "/img/vitaminC_Intensive/Vitamin_C_Serum1.jpg", type: "image" },
  { src: "/img/lemonKojic/lemonkojic1.png", type: "image" },
];

const glutaCarrotPrimaryGallery = [
  {
    src: "/img/whatsappImages/gluta-carrot/Glutacarrot1.jpeg",
    type: "image",
  },
  {
    src: "/img/whatsappImages/gluta-carrot/Glutacarrot2.jpeg",
    type: "image",
  },
  {
    src: "/img/whatsappImages/gluta-carrot/Gluttacarrot3.jpeg",
    type: "image",
  },
  {
    src: "/img/whatsappImages/gluta-carrot/Glutacarrot4.jpeg",
    type: "image",
  },
];

const glutaDiamondPrimaryGallery = [
  {
    src: "/img/whatsappImages/gluta-double/gluta-double-1.jpeg",
    type: "image",
  },
  {
    src: "/img/whatsappImages/gluta-double/gluta-double-2.jpeg",
    type: "image",
  },
  {
    src: "/img/whatsappImages/gluta-double/gluta-double-3.jpeg",
    type: "image",
  },
  {
    src: "/img/whatsappImages/gluta-double/gluta-double-4.jpeg",
    type: "image",
  },
];

const folderGalleryCells = [
  {
    title: "Gluta Carrot Collection",
    price: "$200",
    body: "Radiant glow in every drop — a brightening body collection with glutathione and carrot extract to nourish, even tone, and reveal a healthier, luminous finish.",
    productId: 23,
    src: "/img/whatsappImages/gluta-carrot/Glutacarrot1.jpeg",
    groupKey: "gluta-carrot",
    gallery: glutaCarrotPrimaryGallery,
  },
  {
    title: "Gluta Diamond Series",
    price: "$200",
    body: "A luxury brightening skincare set created to hydrate deeply, enhance clarity, and reveal a healthy, radiant glow.",
    productId: 22,
    src: "/img/whatsappImages/gluta-double/gluta-double-1.jpeg",
    groupKey: "gluta-diamond",
    gallery: glutaDiamondPrimaryGallery,
  },
  {
    title: "Luminous Body Oil Set",
    price: "$180",
    body: "A 4-piece glow set bundling Luminous Body Milk & Body Oil, Bee Honey Soap, Vitamin C Intensive Serum, and Kojic Acid Turmeric & Lemon Body Scrub.",
    productId: 32,
    src: "/img/luminous_set/luminous_set_collage.jpg",
    groupKey: "luminous-body-oil",
    gallery: luminousBodyOilSetGallery,
  },
  {
    title: "Vitamin C Intensive",
    price: "$20",
    body: "Serum visuals from the vitaminC_Intensive folder.",
    productId: 18,
    src: "/img/vitaminC_Intensive/Vitamin_C_Serum1.jpg",
    groupKey: "vitamin-c-intensive-card-two",
    gallery: [
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum1.jpg", type: "image" },
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum.jpg", type: "image" },
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum2.jpg", type: "image" },
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum3.jpg", type: "image" },
    ],
  },
  {
    title: "Vitamin C Plus Cream",
    price: "$25",
    body: "Glass-Cream Solution that moisturizes, rejuvenates, and promotes radiant glass skin.",
    productId: 14,
    src: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
    groupKey: "vitaminc-plus-card-two",
    gallery: [
      {
        src: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_C_plus_Face_cream2.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_c_plus_face_cream3.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_c_plus_face_cream.MP4",
        type: "video",
        poster: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
      },
    ],
  },
  {
    title: "Moroccan Black Soap",
    price: "$25",
    body: "Traditional cleansing soap visuals from the moroccanBlackSoap folder.",
    productId: 24,
    src: "/img/moroccanBlackSoap/moroc.jpeg",
    groupKey: "moroccan-black-soap",
    gallery: [
      { src: "/img/moroccanBlackSoap/moroc.jpeg", type: "image" },
      { src: "/img/moroccanBlackSoap/moroc2.jpeg", type: "image" },
      { src: "/img/moroccanBlackSoap/moroc3.jpeg", type: "image" },
      { src: "/img/moroccanBlackSoap/moroc4.jpeg", type: "image" },
    ],
  },
  {
    title: "Molato Whitening Body Cream",
    price: "$40",
    body: "A rich 500g body cream for deep hydration and a brighter, more even-looking skin tone.",
    productId: 28,
    src: "/img/molato/molatoC.jpeg",
    groupKey: "molato-body-cream",
    gallery: [
      { src: "/img/molato/molatoC.jpeg", type: "image" },
      { src: "/img/molato/molatoC2.jpeg", type: "image" },
      { src: "/img/molato/molatoC3.jpeg", type: "image" },
      { src: "/img/molato/molatoc3in1.jpeg", type: "image" },
      { src: "/img/molato/molatoc4.jpeg", type: "image" },
    ],
  },
  {
    title: "Molato Oil",
    price: "$30",
    body: "Body oil visuals from the 5Dmolato folder.",
    productId: 25,
    src: "/img/5Dmolato/moroc5doil.jpeg",
    groupKey: "5d-molato",
    gallery: [
      { src: "/img/5Dmolato/moroc5doil.jpeg", type: "image" },
      { src: "/img/5Dmolato/moroc5doil2.jpeg", type: "image" },
      { src: "/img/5Dmolato/moroc5doil3.jpeg", type: "image" },
      { src: "/img/5Dmolato/moroc5doil4.jpeg", type: "image" },
      { src: "/img/5Dmolato/moroc5doil5.jpeg", type: "image" },
      { src: "/img/5Dmolato/moroc5doil6.jpeg", type: "image" },
    ],
  },
  {
    title: "Lemon Kojic Scrub",
    price: "$10",
    body: "Exfoliating scrub imagery from the lemonKojic folder.",
    productId: 17,
    src: "/img/lemonKojic/lemonkojic.png",
    groupKey: "lemon-kojic",
    gallery: [
      { src: "/img/lemonKojic/lemonkojic.png", type: "image" },
      { src: "/img/lemonKojic/lemonkojic1.png", type: "image" },
      { src: "/img/lemonKojic/lemonkojic2.png", type: "image" },
    ],
  },
];

const sharedFolderThumbnailOverrides =
  folderGalleryCells.map(cellToThumbnailOverride);

const sharedFolderCaptionOverrides =
  folderGalleryCells.map(cellToCaptionOverride);

const rowTwoColumnOneFolderCells = [
  {
    title: "Papaya Vit C",
    price: "$10",
    body: "Papaya and Vitamin C brightening care from the papaya_vit_c folder.",
    productId: 10,
    src: "/img/papaya_vit_c/papaya_vit_C.JPG",
    groupKey: "papaya-vit-c",
    gallery: [
      { src: "/img/papaya_vit_c/papaya_vit_C.JPG", type: "image" },
      {
        src: "/img/papaya_vit_c/papaya_vit_C_Video.MP4",
        type: "video",
        poster: "/img/papaya_vit_c/papaya_vit_C.JPG",
      },
    ],
  },
  {
    title: "Vitamin C Glow Trio Set",
    price: "$75",
    body: "Three-piece brightening set visuals from the Glow Trio assets.",
    productId: 4,
    src: "/img/trio_set/3set.png",
    groupKey: "vitamin-c-glow-trio",
    gallery: [
      { src: "/img/trio_set/3set.png", type: "image" },
      { src: "/img/trio_set/3set_human.png", type: "image" },
    ],
  },
  {
    title: "Vitamin C Body Butter",
    price: "$25",
    body: "Brightening body butter imagery from the Vitamin_C_Body_Butter folder.",
    productId: 5,
    src: "/img/Vitamin_C_Body_Butter/Vitamin_C_BBB.png",
    groupKey: "vitamin-c-body-butter",
    gallery: [
      { src: "/img/Vitamin_C_Body_Butter/Vitamin_C_BBB.png", type: "image" },
      { src: "/img/Vitamin_C_Body_Butter/Vitamin_C_BBB2.png", type: "image" },
      { src: "/img/Vitamin_C_Body_Butter/Vitamin_C_BBB3.png", type: "image" },
    ],
  },
  {
    title: "Vitamin C Glow Body Oil",
    price: "$30",
    body: "Radiance-boosting body oil infused with Vitamin C to hydrate, brighten, and revive dull skin.",
    productId: 1,
    src: "/img/Vitamin_C_Glow_Body_Oil/Vitamin_C_GBO.png",
    groupKey: "vitamin-c-glow-oil",
    gallery: [
      { src: "/img/Vitamin_C_Glow_Body_Oil/Vitamin_C_GBO.png", type: "image" },
      { src: "/img/Vitamin_C_Glow_Body_Oil/Vitamin_C_GBO2.png", type: "image" },
      { src: "/img/Vitamin_C_Glow_Body_Oil/Vitamin_C_GBO3.png", type: "image" },
      { src: "/img/Vitamin_C_Glow_Body_Oil/Vitamin_C_GBO4.png", type: "image" },
    ],
  },
  {
    title: "Vitamin C Intensive",
    price: "$20",
    body: "Serum visuals from the vitaminC_Intensive folder.",
    productId: 18,
    src: "/img/vitaminC_Intensive/Vitamin_C_Serum1.jpg",
    groupKey: "vitamin-c-intensive",
    gallery: [
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum1.jpg", type: "image" },
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum.jpg", type: "image" },
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum2.jpg", type: "image" },
      { src: "/img/vitaminC_Intensive/Vitamin_C_Serum3.jpg", type: "image" },
    ],
  },
  {
    title: "Vitamin C Plus Cream",
    price: "$25",
    body: "Glass-Cream Solution that moisturizes, rejuvenates, and promotes radiant glass skin.",
    productId: 14,
    src: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
    groupKey: "vitaminc-plus",
    gallery: [
      {
        src: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_C_plus_Face_cream2.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_c_plus_face_cream3.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_c_plus_face_cream.MP4",
        type: "video",
        poster: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
      },
    ],
  },
  {
    title: "Glowing Turmeric",
    price: "$25",
    body: "Body butter visuals from the glowingTumeric folder.",
    productId: 2,
    src: "/img/glowingTumeric/GlowingTumericBB.png",
    groupKey: "glowing-turmeric",
    gallery: [
      { src: "/img/glowingTumeric/GlowingTumericBB.png", type: "image" },
      { src: "/img/glowingTumeric/GlowingTumericBB2.png", type: "image" },
      { src: "/img/glowingTumeric/GlowingTumericBB3_Human.png", type: "image" },
    ],
  },
  {
    title: "Kojic Folder",
    price: "$15",
    body: "Kojic visuals from the WhatsApp kojic folder.",
    productId: 30,
    src: "/img/kojic/kk1.jpeg",
    groupKey: "kojic-whatsapp-row-two",
    gallery: [
      { src: "/img/kojic/kk1.jpeg", type: "image" },
      { src: "/img/kojic/kk2.jpeg", type: "image" },
      { src: "/img/kojic/kk3.jpeg", type: "image" },
      { src: "/img/kojic/kk4.jpeg", type: "image" },
      {
        src: "/img/kojic/kkV.mp4",
        type: "video",
        poster: "/img/kojic/kk1.jpeg",
      },
    ],
  },
  {
    title: "Bee Honey Soap",
    price: "$15",
    body: "Raw honey and propolis soap for gentle cleansing and soft, moisturized skin.",
    productId: 12,
    src: "/img/bee_honey_soap/all_natural_bee_honey_2.JPG",
    groupKey: "bee-honey-soap-row-two",
    gallery: [
      { src: "/img/bee_honey_soap/all_natural_bee_honey_2.JPG", type: "image" },
      {
        src: "/img/bee_honey_soap/all_natural_bee_honey.MP4",
        type: "video",
        poster: "/img/bee_honey_soap/all_natural_bee_honey_2.JPG",
      },
    ],
  },
];

const rowTwoColumnOneThumbnailOverrides =
  rowTwoColumnOneFolderCells.map(cellToThumbnailOverride);

const rowTwoColumnOneCaptionOverrides =
  rowTwoColumnOneFolderCells.map(cellToCaptionOverride);

const rowTwoColumnTwoFolderCells = [
  {
    title: "Eye Lashes",
    price: "$12",
    body: "Wispy lash looks with photo and motion previews from the eye-lashes folder.",
    productId: 13,
    src: "/img/eye_lashes/Eye_lash.JPG",
    groupKey: "eye-lashes-row-two",
    gallery: [
      { src: "/img/eye_lashes/Eye_lash.JPG", type: "image" },
      {
        src: "/img/eye_lashes/Eye_lash.MP4",
        type: "video",
        poster: "/img/eye_lashes/Eye_lash.JPG",
      },
      {
        src: "/img/eye_lashes/Eyel_lash2.MP4",
        type: "video",
        poster: "/img/eye_lashes/Eye_lash.JPG",
      },
    ],
  },
  {
    title: "White Now",
    price: "$12",
    body: "Brightening soap visuals from the white_now folder.",
    productId: 11,
    src: "/img/white_now/white_Now.JPG",
    groupKey: "white-now-row-two",
    gallery: [
      { src: "/img/white_now/white_Now.JPG", type: "image" },
      { src: "/img/white_now/white_Now2.JPG", type: "image" },
      { src: "/img/white_now/white_now_with_soap.JPG", type: "image" },
    ],
  },
  {
    title: "Lash Adhesive",
    price: "$13",
    body: "Rapid-bond lash glue visuals from the lashGlue folder.",
    productId: 16,
    src: "/img/lashGlue/eyeLashGlue1.png",
    groupKey: "lash-glue-row-two",
    gallery: [
      { src: "/img/lashGlue/eyeLashGlue1.png", type: "image" },
      { src: "/img/lashGlue/eyeLashGlue2.png", type: "image" },
      { src: "/img/lashGlue/eyeLashGlue.png", type: "image" },
      { src: "/img/lashGlue/eyeLashGlue4.png", type: "image" },
    ],
  },
  {
    title: "Hair Oil",
    price: "$25",
    body: "Hair growth oil visuals from the HairOil folder.",
    productId: 9,
    src: "/img/HairOil/hairOil.png",
    groupKey: "hair-oil-row-two",
    gallery: [
      { src: "/img/HairOil/hairOil.png", type: "image" },
      { src: "/img/HairOil/hairOil2.png", type: "image" },
      { src: "/img/HairOil/hairOil3.png", type: "image" },
    ],
  },
  {
    title: "Turmeric Soap",
    price: "$10",
    body: "Face soap visuals from the Tumeric_Soap folder.",
    productId: 8,
    src: "/img/Tumeric_Soap/Tumeric_Face_Soap.png",
    groupKey: "turmeric-soap-row-two",
    gallery: [
      { src: "/img/Tumeric_Soap/Tumeric_Face_Soap.png", type: "image" },
      { src: "/img/Tumeric_Soap/Tumeric_Face_Soap_2.png", type: "image" },
      { src: "/img/Tumeric_Soap/Tumeric_face_soap_Human.png", type: "image" },
    ],
  },
  {
    title: "Beauty Sun Screen",
    price: "$20",
    body: "A lightweight SPF50+ facial sunscreen with high UV protection and a non-greasy finish.",
    productId: 26,
    src: "/img/ss/ss2.jpeg",
    groupKey: "ss-row-two",
    gallery: [
      { src: "/img/ss/ss2.jpeg", type: "image" },
      { src: "/img/ss/ss3.jpeg", type: "image" },
      { src: "/img/ss/ss4.jpeg", type: "image" },
    ],
  },
  {
    title: "Whitening Molato Soap",
    price: "$20",
    body: "Extra-strength whitening soap in a 500g jar for deep body cleansing and a brighter, more even-looking skin tone.",
    productId: 27,
    src: "/img/molatoSoap/molato1.jpeg",
    groupKey: "molato-soap-row-two",
    gallery: [
      { src: "/img/molatoSoap/molato1.jpeg", type: "image" },
      { src: "/img/molatoSoap/molatoS.jpeg", type: "image" },
    ],
  },
  {
    title: "Kojic Acid Soap",
    price: "$8",
    body: "Kojic soap photos and video from the Kojic_Acid folder.",
    productId: 7,
    src: "/img/Kojic_Acid/kojicAcid1.png",
    groupKey: "kojic-acid-row-two",
    gallery: [
      { src: "/img/Kojic_Acid/kojicAcid1.png", type: "image" },
      { src: "/img/Kojic_Acid/kojic_acid.png", type: "image" },
      {
        src: "/img/whatsappImages/kojic/kkV.mp4",
        type: "video",
        poster: "/img/Kojic_Acid/kojicAcid1.png",
      },
    ],
  },
  {
    title: "Molato Whitening Body Cream",
    price: "$40",
    body: "A rich 500g body cream for deep hydration and a brighter, more even-looking skin tone.",
    productId: 28,
    src: "/img/molato/molatoC.jpeg",
    groupKey: "molato-row-two",
    gallery: [
      { src: "/img/molato/molatoC.jpeg", type: "image" },
      { src: "/img/molato/molatoC2.jpeg", type: "image" },
      { src: "/img/molato/molatoC3.jpeg", type: "image" },
      { src: "/img/molato/molatoc3in1.jpeg", type: "image" },
      { src: "/img/molato/molatoc4.jpeg", type: "image" },
    ],
  },
];

const rowTwoColumnTwoThumbnailOverrides =
  rowTwoColumnTwoFolderCells.map(cellToThumbnailOverride);

const rowTwoColumnTwoCaptionOverrides =
  rowTwoColumnTwoFolderCells.map(cellToCaptionOverride);

// Cell layout for the row-1, column-2 card (homepage card 5 — "New" badge).
// Gluta Diamond / Gluta Carrot live only on card 2 (Best Seller mosaic).
const rowOneColumnTwoThumbnailOverrides = [
  {
    src: "/img/bee_honey_soap/all_natural_bee_honey_2.JPG",
    productId: 12,
    groupKey: "bee-honey-soap",
    gallery: [
      { src: "/img/bee_honey_soap/all_natural_bee_honey_2.JPG", type: "image" },
      {
        src: "/img/bee_honey_soap/all_natural_bee_honey.MP4",
        type: "video",
        poster: "/img/bee_honey_soap/all_natural_bee_honey_2.JPG",
      },
    ],
  }, // 0 — top-left
  {
    src: "/img/vitcserum/vitcserum.JPG",
    productId: 15,
    groupKey: "vitcserum-row-two",
    gallery: [
      { src: "/img/vitcserum/vitcserum.JPG", type: "image" },
      { src: "/img/vitcserum/vitcserum2.JPG", type: "image" },
      {
        src: "/img/vitcserum/vitamin_c_serum.MP4",
        type: "video",
        poster: "/img/vitcserum/vitcserum.JPG",
      },
    ],
  }, // 1 — top-middle
  {
    src: "/img/GlowingTumeric_BO/GlowingTumericBO_Green_Leaves.png",
    productId: 6,
    groupKey: "glowing-turmeric-oil",
    gallery: [
      {
        src: "/img/GlowingTumeric_BO/GlowingTumericBO_Green_Leaves.png",
        type: "image",
      },
      { src: "/img/GlowingTumeric_BO/GlowingTumericBO.svg", type: "image" },
    ],
  }, // 2 — top-right
  {
    src: "/img/ss/ss2.jpeg",
    productId: 26,
    groupKey: "beauty-sun-screen-card-five",
    gallery: [
      { src: "/img/ss/ss2.jpeg", type: "image" },
      { src: "/img/ss/ss3.jpeg", type: "image" },
      { src: "/img/ss/ss4.jpeg", type: "image" },
    ],
  }, // 3 — middle-left (shifted from 4)
  {
    src: "/img/molatoSoap/molato1.jpeg",
    productId: 27,
    groupKey: "molato-soap-card-five",
    gallery: [
      { src: "/img/molatoSoap/molato1.jpeg", type: "image" },
      { src: "/img/molatoSoap/molatoS.jpeg", type: "image" },
    ],
  }, // 4 — middle-center (shifted from 5)
  {
    src: "/img/papaya_vit_c/papaya_vit_C.JPG",
    productId: 10,
    groupKey: "papaya-vit-c-card-five",
    gallery: [
      { src: "/img/papaya_vit_c/papaya_vit_C.JPG", type: "image" },
      {
        src: "/img/papaya_vit_c/papaya_vit_C_Video.MP4",
        type: "video",
        poster: "/img/papaya_vit_c/papaya_vit_C.JPG",
      },
    ],
  }, // 5 — Papaya Vit C (replaces Gluta Diamond — featured on Best Seller card)
  {
    src: "/img/HairOil/hairOil.png",
    productId: 9,
    groupKey: "hair-oil-card-five",
    gallery: [
      { src: "/img/HairOil/hairOil.png", type: "image" },
      { src: "/img/HairOil/hairOil2.png", type: "image" },
      { src: "/img/HairOil/hairOil3.png", type: "image" },
    ],
  }, // 6 — Hair Oil (replaces Gluta Carrot — featured on Best Seller card)
  {
    src: "/img/vitaminc_plus/vitamin_C_plus_Face_cream2.JPG",
    productId: 14,
    groupKey: "vitaminc-plus-card-five",
    gallery: [
      {
        src: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_C_plus_Face_cream2.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_c_plus_face_cream3.JPG",
        type: "image",
      },
      {
        src: "/img/vitaminc_plus/vitamin_c_plus_face_cream.MP4",
        type: "video",
        poster: "/img/vitaminc_plus/vitamic_C_plus_Face_cream.JPG",
      },
    ],
  }, // 7 — Vitamin C Plus Cream
  {
    src: "/img/luminous_set/luminous_set_collage.jpg",
    productId: 32,
    groupKey: "luminous-body-oil-card-five",
    gallery: luminousBodyOilSetGallery,
  }, // 8 — Luminous Body Oil Set
];

// Caption per cell in the row-1, column-2 card. Each entry lines up 1:1 with
// the matching index in `rowOneColumnTwoThumbnailOverrides`, so the caption
// always describes whichever product set that cell's photo belongs to.
const vitaminCPlusCreamCaption = {
  title: "Vitamin C Plus Cream",
  price: "$25",
  body: "Glass-Cream Solution that moisturizes, rejuvenates, and promotes radiant glass skin.",
};

const luminousBodyOilCaption = {
  title: "Luminous Body Oil Set",
  price: "$180",
  body: "A 4-piece glow set bundling Luminous Body Milk & Body Oil, Bee Honey Soap, Vitamin C Intensive Serum, and Kojic Acid Turmeric & Lemon Body Scrub.",
};

const beeHoneySoapCaption = {
  title: "Bee Honey Soap",
  price: "$15",
  body: "Raw honey and propolis soap for gentle cleansing and soft, moisturized skin.",
};

const vitCSerumCaption = {
  title: "Vit C Serum",
  price: "$25",
  body: "Serum visuals from the vitcserum folder.",
};

const glowingTurmericOilCaption = {
  title: "Glowing Turmeric Oil",
  price: "$35",
  body: "Lightweight body oil imagery pulled from the GlowingTumeric_BO folder.",
};

const beautySunScreenCaption = {
  title: "Beauty Sun Screen",
  price: "$20",
  body: "A lightweight SPF50+ PA+++ facial sunscreen designed for high sun protection with a non-greasy finish.",
};

const whiteningMolatoSoapCaption = {
  title: "Whitening Molato Soap",
  price: "$20",
  body: "An extra-strength Molato cleansing soap in a large-format jar for brightening-focused body-care routines.",
};

const papayaVitCCaption = {
  title: "Papaya Vit C",
  price: "$10",
  body: "Papaya and Vitamin C brightening care from the papaya_vit_c folder.",
};

const hairOilCaption = {
  title: "Hair Oil",
  price: "$25",
  body: "Hair growth oil visuals from the HairOil folder.",
};

const rowOneColumnTwoCaptionOverrides = [
  beeHoneySoapCaption, // 0 — Bee Honey Soap (full gallery)
  vitCSerumCaption, // 1 — Vit C Serum (full gallery)
  glowingTurmericOilCaption, // 2 — Glowing Turmeric Oil
  beautySunScreenCaption, // 3 — Beauty Sun Screen (shifted from 4)
  whiteningMolatoSoapCaption, // 4 — Whitening Molato Soap (shifted from 5)
  papayaVitCCaption, // 5 — Papaya Vit C
  hairOilCaption, // 6 — Hair Oil
  vitaminCPlusCreamCaption, // 7 — Vitamin C Plus Cream
  luminousBodyOilCaption, // 8 — Luminous Body Oil
];

const homepageOverrideMap = {
  2: {
    thumbnails: sharedFolderThumbnailOverrides,
    captions: sharedFolderCaptionOverrides,
  },
  5: {
    thumbnails: rowOneColumnTwoThumbnailOverrides,
    captions: rowOneColumnTwoCaptionOverrides,
  },
  12: {
    thumbnails: rowTwoColumnOneThumbnailOverrides,
    captions: rowTwoColumnOneCaptionOverrides,
  },
  4: {
    thumbnails: rowTwoColumnTwoThumbnailOverrides,
    captions: rowTwoColumnTwoCaptionOverrides,
  },
};

const isVideoSrc = (src) =>
  typeof src === "string" && /\.(mp4|mov|webm|m4v)$/i.test(src);

const buildOverrideCellPreview = (thumbnail, caption, productList) => {
  const product = productList.find((entry) => entry.id === thumbnail.productId);
  if (!product) return null;

  const galleryImages =
    Array.isArray(thumbnail.gallery) && thumbnail.gallery.length > 0
      ? thumbnail.gallery.map((item) => ({
          image: item.src,
          mediaType: item.type || (isVideoSrc(item.src) ? "video" : "image"),
          poster: item.poster || product.image,
        }))
      : [{ image: thumbnail.src, mediaType: "image", poster: product.image }];

  const activePreviewIndex = galleryImages.findIndex(
    (item) => item.image === thumbnail.src,
  );

  return {
    image: thumbnail.src,
    mediaType: "image",
    poster: thumbnail.poster,
    activePreviewIndex: activePreviewIndex >= 0 ? activePreviewIndex : 0,
    caption,
    product,
    galleryImages,
  };
};

const HomePage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [preview, setPreview] = useState(() => buildDefaultPreview(products));
  // Controls the mobile-only floating preview overlay. We don't auto-open
  // on first render — only when the user explicitly clicks a cell — so the
  // featured default preview still drives the desktop sticky pane silently.
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const [activeMedicalCellIndex, setActiveMedicalCellIndex] = useState(null);
  const [activeMedicalHalfIndex, setActiveMedicalHalfIndex] = useState(null);
  const [activeFreeGiftCellIndex, setActiveFreeGiftCellIndex] = useState(null);
  const previewPaneRef = useRef(null);
  const searchRef = useRef(null);
  const [desktopPreviewHeight, setDesktopPreviewHeight] = useState(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();

  // Matches Tailwind's `lg` breakpoint (1024px). Used to ensure the mobile
  // overlays + the body-scroll lock are only ever active on small screens —
  // otherwise clicking a cell on desktop would freeze page scroll because
  // the state would flip even though the overlay itself is `lg:hidden`.
  const isMobileViewport = useCallback(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1023px)").matches,
    [],
  );

  // Wrapper passed to every ProductCard. Updates the preview as before and,
  // only on mobile viewports, additionally pops the floating overlay to the
  // front so touch users can actually see + interact with the preview they
  // just tapped. Desktop keeps using the sticky right-column pane.
  const handleSelectPreview = useCallback(
    (next) => {
      setPreview(next);
      if (isMobileViewport()) {
        setIsMobilePreviewOpen(true);
      }
    },
    [isMobileViewport],
  );

  const closeMobilePreview = useCallback(() => {
    setIsMobilePreviewOpen(false);
  }, []);

  const openProductDetails = useCallback((productId) => {
    const product = products.find((entry) => entry.id === productId);
    setIsMobilePreviewOpen(false);
    navigate(product ? getProductPath(product) : `/product/${productId}`);
  }, [navigate]);

  useEffect(() => {
    const closeSearch = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", closeSearch);
    return () => document.removeEventListener("mousedown", closeSearch);
  }, []);

  // Lock background scroll while the floating preview is open. Full product
  // details use their normal route instead of nesting a page inside a dialog.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!isMobilePreviewOpen) return undefined;

    const mql = window.matchMedia("(max-width: 1023px)");
    const previousOverflow = document.body.style.overflow;

    const apply = () => {
      document.body.style.overflow = mql.matches ? "hidden" : previousOverflow;
    };
    apply();
    mql.addEventListener("change", apply);

    return () => {
      mql.removeEventListener("change", apply);
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobilePreviewOpen]);

  // Keep the desktop preview fully inside the visible viewport both before
  // and after the column reaches its sticky position.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let frameId;
    const measurePreviewPane = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        if (!previewPaneRef.current || window.innerWidth < 1024) {
          setDesktopPreviewHeight(null);
          return;
        }

        const top = Math.max(previewPaneRef.current.getBoundingClientRect().top, 0);
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const availableHeight = Math.max(320, Math.floor(viewportHeight - top - 16));
        setDesktopPreviewHeight((current) =>
          current === availableHeight ? current : availableHeight,
        );
      });
    };

    measurePreviewPane();
    window.addEventListener("scroll", measurePreviewPane, { passive: true });
    window.addEventListener("resize", measurePreviewPane);
    window.visualViewport?.addEventListener("resize", measurePreviewPane);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", measurePreviewPane);
      window.removeEventListener("resize", measurePreviewPane);
      window.visualViewport?.removeEventListener("resize", measurePreviewPane);
    };
  }, []);

  const handlePreviewLike = (product) => {
    toast.success(`You liked ${product.name}`);
  };

  const handlePreviewLove = (product) => {
    toggleFavorite(product);
  };

  const handlePreviewShare = async (product) => {
    const shareUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${getProductPath(product)}`
        : getProductPath(product);
    const shareData = {
      title: product.name,
      text: product.description || `Check out ${product.name} from JmoLux`,
      url: shareUrl,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
      } else if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Product link copied to clipboard");
      } else {
        toast.info(shareUrl);
      }
    } catch (err) {
      if (err && err.name !== "AbortError") {
        toast.error("Could not share — try copying the link instead");
      }
    }
  };
  const homepagePriority = [2, 5, 12, 4, 6, 3, 1];
  const homepagePriorityMap = new Map(
    homepagePriority.map((id, index) => [id, index]),
  );
  const HIDDEN_HOMEPAGE_CATEGORIES = new Set(["medical supplies", "scrub set"]);
  const MIN_HOMEPAGE_PRODUCTS = 4;
  const sortHomepageProducts = (productList) =>
    productList.slice().sort((a, b) => {
      const aRank = homepagePriorityMap.has(a.id)
        ? homepagePriorityMap.get(a.id)
        : Number.MAX_SAFE_INTEGER;
      const bRank = homepagePriorityMap.has(b.id)
        ? homepagePriorityMap.get(b.id)
        : Number.MAX_SAFE_INTEGER;

      if (aRank !== bRank) {
        return aRank - bRank;
      }

      return a.id - b.id;
    });

  const visibleProducts = products.filter(
    (product) =>
      isProductVisible(product) &&
      !HIDDEN_HOMEPAGE_CATEGORIES.has(product.mainCategories),
  );

  const selectedProducts =
    selectedCategory === "all"
      ? visibleProducts
      : visibleProducts.filter(
          (product) => product.mainCategories === selectedCategory,
        );

  const filteredProducts = (() => {
    const uniqueProducts = [];
    const seenProducts = new Set();
    const addUnique = (productList) => {
      productList.forEach((product) => {
        const productKey = getHomepageProductKey(product);
        if (
          !seenProducts.has(productKey) &&
          uniqueProducts.length < MIN_HOMEPAGE_PRODUCTS
        ) {
          seenProducts.add(productKey);
          uniqueProducts.push(product);
        }
      });
    };

    addUnique(sortHomepageProducts(selectedProducts));

    if (uniqueProducts.length < MIN_HOMEPAGE_PRODUCTS) {
      addUnique(sortHomepageProducts(visibleProducts));
    }

    return uniqueProducts;
  })();

  const allCategories = [
    "all",
    ...categoryList
      .map((category) => category.name)
      .filter((name) => {
        const lower = name.toLowerCase().trim();
        return lower !== "scrub set" && lower !== "medical supplies";
      }),
  ];

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchResults = normalizedSearchQuery
    ? products
        .filter(isProductVisible)
        .filter((product) =>
          [
            product.name,
            getProductDisplayName(product),
            product.category,
            product.mainCategories,
          ]
            .filter(Boolean)
            .some((value) =>
              String(value).toLowerCase().includes(normalizedSearchQuery),
            ),
        )
        .slice(0, 5)
    : [];

  const selectSearchResult = (productId) => {
    const product = products.find((entry) => entry.id === productId);
    setSearchQuery("");
    setIsSearchOpen(false);
    navigate(product ? getProductPath(product) : `/product/${productId}`);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchResults[0]) {
      selectSearchResult(searchResults[0].id);
    }
  };

  const faqItems = [
    {
      question: "What kinds of products does JmoLux sell?",
      answer:
        "JmoLux offers skincare, body care, haircare, scrub sets, beauty essentials and selected medical supplies.",
    },
    {
      question: "Can I browse products by collection?",
      answer:
        "Yes. Collections now have dedicated category pages so shoppers can browse by skincare, haircare, scrub sets and more.",
    },
    {
      question: "How can I contact JmoLux before ordering?",
      answer:
        "You can use the contact page or email hello@jmolux.com for product questions, order help and support.",
    },
  ];

  // Shared preview card body. Rendered both in the desktop sticky aside and
  // inside the mobile full-screen overlay so tapping a cell on a phone pops
  // up the same rich, interactive preview the desktop pane shows.
  //
  // Note on the cell-preview <video> below: the optimize-images script can
  // generate a `<name>.web.mp4` (720p H.264 + faststart) sibling, and the
  // `videoOptimized()` helper exposes it — but swapping the video `src` is a
  // behavior change (different codec params, no audio track) so we leave
  // that work for a follow-up. The `<video poster>` is left as `preview.poster`
  // intentionally for the same reason.
  const previewWebp480 = preview ? webpVariant(preview.image, 480) : undefined;
  const previewWebp960 = preview ? webpVariant(preview.image, 960) : undefined;
  const previewWebpFull = preview ? webpVariant(preview.image) : undefined;
  const previewWebpSrcSet = previewWebp960
    ? `${previewWebp480 ?? previewWebp960} 480w, ${previewWebp960} 960w${
        previewWebpFull ? `, ${previewWebpFull} 1600w` : ""
      }`
    : "";
  const previewCardBody = preview ? (
    <>
      {/* 1. Media — image or video depending on what was clicked */}
      <div className="relative aspect-square bg-gray-100">
        {preview.mediaType === "video" ? (
          <video
            key={preview.image}
            src={preview.image}
            poster={preview.poster}
            controls
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full object-cover bg-black"
          />
        ) : (
          <picture>
            {previewWebp960 && (
              <source
                type="image/webp"
                srcSet={previewWebpSrcSet}
                sizes="(min-width: 1024px) 480px, 100vw"
              />
            )}
            <img
              src={preview.image}
              alt={preview.product.name}
              className="absolute inset-0 h-full w-full object-cover"
              // This <img> is the desktop LCP on first paint (the preview pane
              // is sticky and visible above the fold on lg+ viewports). Eager
              // + high-priority hints let the browser parallelise the fetch
              // with critical CSS/JS instead of waiting for layout. Harmless
              // on subsequent preview changes — the browser ignores the hints
              // for an already-loaded resource.
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        )}
        <span className="absolute top-3 left-3 bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md shadow">
          {preview.mediaType === "video" ? "Now Playing" : "Preview"}
        </span>
      </div>

      {Array.isArray(preview.galleryImages) &&
        preview.galleryImages.length > 1 && (
          <div className="px-5 pt-4 pb-2 border-b border-amber-100">
            <div className="grid grid-cols-4 gap-2">
              {preview.galleryImages.map((item, index) => {
                const isActiveMedia =
                  typeof preview.activePreviewIndex === "number"
                    ? preview.activePreviewIndex === index
                    : preview.image === item.image &&
                      preview.mediaType === item.mediaType;

                return (
                  <button
                    key={`${item.image}-${index}`}
                    type="button"
                    onClick={() =>
                      setPreview((current) => ({
                        ...current,
                        image: item.image,
                        mediaType: item.mediaType,
                        poster: item.poster,
                        activePreviewIndex: index,
                      }))
                    }
                    className={`relative aspect-square overflow-hidden rounded-md border-2 transition-all duration-200 ${
                      isActiveMedia
                        ? "border-amber-400 ring-2 ring-amber-400/25"
                        : "border-gray-200 hover:border-amber-300"
                    }`}
                    aria-label={`Preview alternate view ${index + 1}`}
                  >
                    <img
                      src={
                        item.mediaType === "video"
                          ? item.poster || preview.product.image
                          : item.image
                      }
                      alt=""
                      className="h-full w-full object-cover"
                    />
                    {item.mediaType === "video" && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm">
                          <svg
                            className="h-3.5 w-3.5 text-gray-900 ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      {/* 2. Compact purchase card — visible immediately after the media */}
      <div className="px-5 py-3 border-b border-amber-200">
        <div className="flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50/80 p-3 shadow-sm">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">
              {getProductDisplayName(preview.product)}
            </p>
            <div className="mt-0.5 flex items-baseline gap-2">
              <span className="text-lg font-bold text-amber-700">
                {formatPrice(
                  getProductPricing(preview.product).effectivePrice,
                )}
              </span>
              {getProductPricing(preview.product).isOnSale && (
                <span className="text-xs text-gray-400 line-through">
                  {formatPrice(
                    getProductPricing(preview.product).originalPrice,
                  )}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              addToCart(preview.product);
              toast.success(`Added ${preview.product.name} to cart`, {
                duration: ADD_TO_CART_TOAST_DURATION,
              });
            }}
            aria-label="Add to cart"
            title={`Add to cart — ${formatPrice(getProductPricing(preview.product).effectivePrice)}`}
            className="flex shrink-0 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-black shadow-md hover:from-yellow-500 hover:to-yellow-700 hover:shadow-lg active:scale-95 transition-all duration-200"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z"
              />
            </svg>
            Add to Cart
          </button>
        </div>
      </div>

      {/* 3. Secondary quick actions */}
      <div className="px-5 py-2 border-b border-amber-200">
        <div className="grid grid-cols-3 gap-1.5">
          {/* Like (thumbs up) */}
          <button
            type="button"
            onClick={() => handlePreviewLike(preview.product)}
            aria-label="Like this product"
            title="Like"
            className="group flex items-center justify-center py-1.5 rounded-md border border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 active:scale-95 transition-all duration-200"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
              />
            </svg>
          </button>

          {/* Love / Wishlist (heart) — toggles favorite */}
          <button
            type="button"
            onClick={() => handlePreviewLove(preview.product)}
            aria-label={
              isFavorite(preview.product.id)
                ? "Remove from wishlist"
                : "Save to wishlist"
            }
            title={
              isFavorite(preview.product.id)
                ? "In your wishlist"
                : "Save to wishlist"
            }
            className={`group flex items-center justify-center py-1.5 rounded-md border active:scale-95 transition-all duration-200 ${
              isFavorite(preview.product.id)
                ? "border-red-300 bg-red-50 text-red-600"
                : "border-gray-200 text-gray-700 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
            }`}
          >
            <svg
              className="w-3.5 h-3.5"
              fill={isFavorite(preview.product.id) ? "currentColor" : "none"}
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>

          {/* Share */}
          <button
            type="button"
            onClick={() => handlePreviewShare(preview.product)}
            aria-label="Share this product"
            title="Share"
            className="group flex items-center justify-center py-1.5 rounded-md border border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 active:scale-95 transition-all duration-200"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* 4. Product metadata: category, name, full description, key benefits */}
      <div className="p-5 space-y-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
            {preview.product.category}
            {preview.product.mainCategories &&
              ` · ${preview.product.mainCategories}`}
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mt-1 leading-snug">
            {getProductDisplayName(preview.product)}
          </h3>
          {preview.product.sku && (
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">
              SKU: {preview.product.sku}
            </p>
          )}
        </div>

        {/* Full description from product metadata (detailedDescription → fall back to description) */}
        {(preview.product.detailedDescription ||
          preview.product.description) && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1.5">
              Description
            </p>
            <p className="text-[11px] text-gray-700 leading-snug line-clamp-2">
              {preview.product.detailedDescription ||
                preview.product.description}
            </p>
          </div>
        )}

        {/* Key benefits if the product has them */}
        {Array.isArray(preview.product.benefits) &&
          preview.product.benefits.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1.5">
                Key Benefits
              </p>
              <ul className="text-xs text-gray-700 space-y-1 leading-relaxed">
                {preview.product.benefits.slice(0, 5).map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

        {/* Compact spec summary if the product carries one */}
        {preview.product.specifications &&
          typeof preview.product.specifications === "object" && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1.5">
                Details
              </p>
              <dl className="text-xs text-gray-700 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 leading-relaxed">
                {Object.entries(preview.product.specifications)
                  .slice(0, 4)
                  .map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt className="text-gray-500 capitalize">
                        {k.replace(/([A-Z])/g, " $1").trim()}
                      </dt>
                      <dd className="text-gray-800">{v}</dd>
                    </React.Fragment>
                  ))}
              </dl>
            </div>
          )}

        <Button
          variant="gold"
          className="!w-full !py-2 text-xs flex items-center justify-center gap-2"
          onClick={() => {
            addToCart(preview.product);
            toast.success(`Added ${preview.product.name} to cart`, {
              duration: ADD_TO_CART_TOAST_DURATION,
            });
          }}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z"
            />
          </svg>
          Add to Cart — {formatPrice(getProductPricing(preview.product).effectivePrice)}
        </Button>

        {/* Full details always use the product route. Keeping a complete page
            inside a second fixed dialog traps touch scrolling on mobile. */}
        <Button
          variant="secondary"
          className="!w-full !py-2 text-xs"
          onClick={() => openProductDetails(preview.product.id)}
        >
          View Full Details
        </Button>
      </div>
    </>
  ) : (
    <div className="p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
        <svg
          className="w-8 h-8 text-amber-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      </div>
      <p className="text-sm font-semibold text-gray-800">
        Click any image to preview
      </p>
      <p className="text-xs text-gray-500 mt-2 leading-relaxed">
        Tap any product photo on the left and see a full-size preview with
        details right here.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black">
      <Seo
        title="Luxury Skincare, Body Care and EveryDay Scrubs"
        description="Shop JmoLux for brightening body creams, body butters, soap collections and EveryDay Scrubs with premium ingredients, fast support and nationwide delivery."
        path="/"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "JmoLux",
            url: typeof window !== "undefined" ? window.location.origin : "/",
            email: "hello@jmolux.com",
            sameAs: [
              typeof window !== "undefined" ? window.location.origin : "/",
            ],
            contactPoint: {
              "@type": "ContactPoint",
              email: "hello@jmolux.com",
              contactType: "customer support",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          },
        ]}
      />
      <main>
        <ScrollingTicker
          label="Skincare and Essentials"
          centerContent={
            <div className="flex items-center gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {allCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`capitalize whitespace-nowrap shrink-0 text-[11px] font-medium px-3 py-1 rounded-full border transition-all duration-200 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-black border-amber-400"
                      : "bg-white/5 text-gray-400 border-white/10 hover:bg-amber-400/10 hover:border-amber-400/30 hover:text-gray-200"
                  }`}
                >
                  {category === "all" ? "All" : category}
                </button>
              ))}
            </div>
          }
        />

        <section className="relative z-30 bg-slate-900 px-4 pt-3 pb-2">
          <div ref={searchRef} className="relative mx-auto w-full max-w-md">
            <form
              role="search"
              onSubmit={handleSearchSubmit}
              className={`flex h-11 items-center rounded-full border bg-slate-950/80 px-4 shadow-[0_8px_30px_rgba(0,0,0,0.22)] backdrop-blur-md transition-all ${
                isSearchOpen
                  ? "border-amber-400/60 ring-2 ring-amber-400/10"
                  : "border-white/10 hover:border-amber-400/35"
              }`}
            >
              <svg
                className="h-4 w-4 shrink-0 text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="m21 21-4.35-4.35m1.35-5.4A6.75 6.75 0 1 1 4.5 11.25a6.75 6.75 0 0 1 13.5 0Z"
                />
              </svg>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setIsSearchOpen(true);
                }}
                onFocus={() => setIsSearchOpen(true)}
                placeholder="Search products..."
                aria-label="Search products"
                className="min-w-0 flex-1 bg-transparent px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none"
              />
              <span className="hidden text-[9px] font-semibold uppercase tracking-[0.18em] text-amber-300/50 sm:block">
                Enter
              </span>
            </form>

            {isSearchOpen && normalizedSearchQuery && (
              <div className="absolute left-0 right-0 top-[calc(100%+8px)] overflow-hidden rounded-2xl border border-amber-400/20 bg-slate-950/95 p-1.5 shadow-2xl backdrop-blur-xl">
                {searchResults.length > 0 ? (
                  searchResults.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => selectSearchResult(product.id)}
                      className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-amber-400/10 focus:bg-amber-400/10 focus:outline-none"
                    >
                      <img
                        src={webpVariant(product.image, 480)}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-lg border border-white/10 bg-white object-contain"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-100">
                          {getProductDisplayName(product)}
                        </span>
                        <span className="block truncate text-[10px] uppercase tracking-wider text-slate-500">
                          {product.category || product.mainCategories}
                        </span>
                      </span>
                      <ProductPrice product={product} size="xs" />
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-5 text-center text-sm text-slate-400">
                    No products found
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Products Grid + Sticky Preview Pane (far-right column on desktop) */}
        <section id="featured-products" className="pt-2 pb-12 relative bg-slate-900">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(251,191,36,0.01)_50%,transparent_100%)]"></div>
          <div className="relative container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
              {/* Products: take cols 1–2 on desktop, internally a 2-col sub-grid */}
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 items-start content-start self-start">
                {filteredProducts.map((product, index) => {
                  const override = homepageOverrideMap[product.id] || {};

                  return (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onImageClick={handleSelectPreview}
                      showLibraryThumbnails={false}
                      libraryProducts={products}
                      libraryOffset={Math.floor(index / 2) * 9}
                      thumbnailMediaOverrides={override.thumbnails || []}
                      captionOverrides={override.captions || []}
                      previewStartsFromFirstImage={false}
                    />
                  );
                })}
                {filteredProducts.length === 0 && (
                  <div className="col-span-full text-center py-10">
                    <div className="w-12 h-12 mx-auto mb-4 bg-gray-800/50 rounded-full flex items-center justify-center border border-amber-400/20">
                      <svg
                        className="w-6 h-6 text-amber-400/60"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-3-3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-400 text-base font-light">
                      No products found in this category.
                    </p>
                  </div>
                )}

                {filteredProducts.length > 0 && (
                  <div className="col-span-full flex flex-col sm:flex-row gap-6 lg:gap-8 items-start w-full">
                    <div className="w-full sm:w-1/2 self-start bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                      <div className="px-3 sm:px-4 pt-3 pb-2">
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                            Medical Supplies
                          </p>
                          {globalMedicalRowCells.some(isMedicalCellOnSale) && (
                            <FlashSalesBadge />
                          )}
                        </div>
                      {/* Same half-width card + grid-cols-3 as ProductCard → matching 3×3 cell size */}
                      <div className="grid grid-cols-3 gap-x-2 gap-y-3 bg-white">
                        {globalMedicalRowCells.map((cell, index) => {
                          if (cell.combined) {
                            const isActive = activeMedicalCellIndex === index;
                            const combinedProduct = products.find(
                              (entry) => entry.id === cell.halves[0].productId,
                            );

                            const openHalfPreview = (half, halfIndex) => {
                              setActiveMedicalCellIndex(index);
                              setActiveMedicalHalfIndex(halfIndex);
                              const thumbnail = cellToThumbnailOverride(half);
                              const caption = cellToCaptionOverride(half);
                              const nextPreview = buildOverrideCellPreview(
                                thumbnail,
                                caption,
                                products,
                              );
                              if (nextPreview) {
                                handleSelectPreview(nextPreview);
                              }
                            };

                            return (
                              <div
                                key="medical-gloves-mask-combined"
                                className="group/cell flex flex-col items-stretch text-left"
                              >
                                <div
                                  className={`relative aspect-square w-full overflow-hidden rounded-md border-2 transition-all duration-200 ${
                                    isActive
                                      ? "border-amber-400 ring-2 ring-amber-400/30"
                                      : "border-gray-200 group-hover/cell:border-amber-300"
                                  }`}
                                >
                                  <div className="relative h-full w-full">
                                    {cell.halves.map((half, halfIndex) => {
                                      const product = products.find(
                                        (entry) => entry.id === half.productId,
                                      );
                                      if (!product) return null;

                                      const isHalfActive =
                                        isActive &&
                                        activeMedicalHalfIndex === halfIndex;
                                      const isThumbnailFavorited = isFavorite(
                                        product.id,
                                      );
                                      const cellPosterSrc = half.src;
                                      const cellWebp480 = webpVariant(
                                        cellPosterSrc,
                                        480,
                                      );
                                      const cellWebp960 = webpVariant(
                                        cellPosterSrc,
                                        960,
                                      );
                                      const cellWebpFull =
                                        webpVariant(cellPosterSrc);
                                      const cellWebpSrcSet = cellWebp960
                                        ? `${cellWebp480 ?? cellWebp960} 480w, ${cellWebp960} 960w${
                                            cellWebpFull
                                              ? `, ${cellWebpFull} 1600w`
                                              : ""
                                          }`
                                        : "";
                                      const isTopLeft = halfIndex === 0;

                                      return (
                                        <div
                                          key={`medical-half-${half.productId}`}
                                          role="button"
                                          tabIndex={0}
                                          onMouseEnter={() => {
                                            setActiveMedicalCellIndex(index);
                                            setActiveMedicalHalfIndex(halfIndex);
                                          }}
                                          onFocus={() => {
                                            setActiveMedicalCellIndex(index);
                                            setActiveMedicalHalfIndex(halfIndex);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key !== "Enter" && e.key !== " ") return;
                                            e.preventDefault();
                                            openHalfPreview(half, halfIndex);
                                          }}
                                          onClick={() =>
                                            openHalfPreview(half, halfIndex)
                                          }
                                          aria-label={`View ${half.title} of ${product.name}`}
                                          className={`absolute inset-0 overflow-hidden cursor-pointer transition-all duration-200 ${
                                            isTopLeft
                                              ? "[clip-path:polygon(0_0,100%_0,0_100%)]"
                                              : "[clip-path:polygon(100%_0,100%_100%,0_100%)]"
                                          } ${
                                            isHalfActive
                                              ? "ring-2 ring-inset ring-amber-400/60"
                                              : ""
                                          }`}
                                        >
                                          <picture className="absolute inset-0 block h-full w-full">
                                            {cellWebp960 && (
                                              <source
                                                type="image/webp"
                                                srcSet={cellWebpSrcSet}
                                                sizes="(min-width: 1024px) 80px, (min-width: 640px) 12vw, 25vw"
                                              />
                                            )}
                                            <img
                                              src={cellPosterSrc}
                                              alt=""
                                              loading="lazy"
                                              decoding="async"
                                              className="block h-full w-full object-cover"
                                            />
                                          </picture>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleFavorite(product);
                                            }}
                                            aria-label={
                                              isThumbnailFavorited
                                                ? `Remove ${product.name} from wishlist`
                                                : `Save ${product.name} to wishlist`
                                            }
                                            title={
                                              isThumbnailFavorited
                                                ? "Remove from wishlist"
                                                : "Save to wishlist"
                                            }
                                            className={`absolute z-10 flex h-4 w-4 items-center justify-center rounded-full border shadow-sm transition-all duration-200 ${
                                              isTopLeft
                                                ? "top-1 left-1"
                                                : "bottom-1 right-1"
                                            } ${
                                              isThumbnailFavorited
                                                ? "border-red-200 bg-white text-red-500"
                                                : "border-white/70 bg-white/90 text-gray-500 hover:text-red-500"
                                            }`}
                                          >
                                            <svg
                                              className="h-2.5 w-2.5"
                                              fill={
                                                isThumbnailFavorited
                                                  ? "currentColor"
                                                  : "none"
                                              }
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                              strokeWidth={2}
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      );
                                    })}
                                    <div
                                      className="pointer-events-none absolute inset-0"
                                      aria-hidden="true"
                                      style={{
                                        background:
                                          "linear-gradient(to bottom right, transparent calc(50% - 0.5px), rgba(229,231,235,0.9) calc(50% - 0.5px), rgba(229,231,235,0.9) calc(50% + 0.5px), transparent calc(50% + 0.5px))",
                                      }}
                                    />
                                  </div>
                                </div>
                                <p
                                  className={`mt-1.5 text-[10px] sm:text-[11px] leading-tight font-semibold line-clamp-1 text-center transition-colors duration-200 ${
                                    isActive
                                      ? "text-amber-600"
                                      : "text-gray-900 group-hover/cell:text-gray-900"
                                  }`}
                                >
                                  {cell.title}
                                </p>
                                <div className="mt-0.5 flex justify-center">
                                  {combinedProduct && (
                                    <ProductPrice
                                      product={combinedProduct}
                                      size="xs"
                                      stacked
                                      suffix=" each"
                                      className="justify-center transition-colors duration-200"
                                    />
                                  )}
                                </div>
                                <p
                                  className={`mt-0.5 text-[8px] sm:text-[9px] leading-snug font-normal line-clamp-2 text-center transition-colors duration-200 ${
                                    isActive
                                      ? "text-amber-700/80"
                                      : "text-gray-600 group-hover/cell:text-gray-700"
                                  }`}
                                >
                                  {cell.body}
                                </p>
                              </div>
                            );
                          }

                          const thumbnail = cellToThumbnailOverride(cell);
                          const caption = cellToCaptionOverride(cell);
                          const product = products.find(
                            (entry) => entry.id === thumbnail.productId,
                          );
                          if (!product) return null;

                          const isActive = activeMedicalCellIndex === index;
                          const isThumbnailFavorited = isFavorite(product.id);
                          const cellPosterSrc = thumbnail.src;
                          const cellWebp480 = webpVariant(cellPosterSrc, 480);
                          const cellWebp960 = webpVariant(cellPosterSrc, 960);
                          const cellWebpFull = webpVariant(cellPosterSrc);
                          const cellWebpSrcSet = cellWebp960
                            ? `${cellWebp480 ?? cellWebp960} 480w, ${cellWebp960} 960w${
                                cellWebpFull ? `, ${cellWebpFull} 1600w` : ""
                              }`
                            : "";

                          return (
                            <div
                              key={`medical-${thumbnail.productId}`}
                              role="button"
                              tabIndex={0}
                              onMouseEnter={() => {
                                setActiveMedicalCellIndex(index);
                                setActiveMedicalHalfIndex(null);
                              }}
                              onFocus={() => {
                                setActiveMedicalCellIndex(index);
                                setActiveMedicalHalfIndex(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key !== "Enter" && e.key !== " ") return;
                                e.preventDefault();
                                setActiveMedicalCellIndex(index);
                                setActiveMedicalHalfIndex(null);
                                const nextPreview = buildOverrideCellPreview(
                                  thumbnail,
                                  caption,
                                  products,
                                );
                                if (nextPreview) {
                                  handleSelectPreview(nextPreview);
                                }
                              }}
                              onClick={() => {
                                setActiveMedicalCellIndex(index);
                                setActiveMedicalHalfIndex(null);
                                const nextPreview = buildOverrideCellPreview(
                                  thumbnail,
                                  caption,
                                  products,
                                );
                                if (nextPreview) {
                                  handleSelectPreview(nextPreview);
                                }
                              }}
                              aria-label={`View ${caption.title} of ${product.name}`}
                              className="group/cell flex flex-col items-stretch text-left cursor-pointer"
                            >
                              <div
                                className={`relative aspect-square w-full overflow-hidden rounded-md border-2 transition-all duration-200 ${
                                  isActive
                                    ? "border-amber-400 ring-2 ring-amber-400/30"
                                    : "border-gray-200 group-hover/cell:border-amber-300"
                                }`}
                              >
                                <picture className="absolute inset-0 block h-full w-full">
                                  {cellWebp960 && (
                                    <source
                                      type="image/webp"
                                      srcSet={cellWebpSrcSet}
                                      sizes="(min-width: 1024px) 160px, (min-width: 640px) 25vw, 50vw"
                                    />
                                  )}
                                  <img
                                    src={cellPosterSrc}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                    className="block h-full w-full object-cover"
                                  />
                                </picture>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(product);
                                  }}
                                  aria-label={
                                    isThumbnailFavorited
                                      ? `Remove ${product.name} from wishlist`
                                      : `Save ${product.name} to wishlist`
                                  }
                                  title={
                                    isThumbnailFavorited
                                      ? "Remove from wishlist"
                                      : "Save to wishlist"
                                  }
                                  className={`absolute top-1 right-1 z-10 flex h-4 w-4 items-center justify-center rounded-full border shadow-sm transition-all duration-200 ${
                                    isThumbnailFavorited
                                      ? "border-red-200 bg-white text-red-500"
                                      : "border-white/70 bg-white/90 text-gray-500 hover:text-red-500"
                                  }`}
                                >
                                  <svg
                                    className="h-2.5 w-2.5"
                                    fill={
                                      isThumbnailFavorited ? "currentColor" : "none"
                                    }
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z"
                                    />
                                  </svg>
                                </button>
                              </div>
                              <p
                                className={`mt-1.5 text-[10px] sm:text-[11px] leading-tight font-semibold line-clamp-1 text-center transition-colors duration-200 ${
                                  isActive
                                    ? "text-amber-600"
                                    : "text-gray-900 group-hover/cell:text-gray-900"
                                }`}
                              >
                                {caption.title}
                              </p>
                              <div className="mt-0.5 flex justify-center">
                                <ProductPrice
                                  product={product}
                                  size="xs"
                                  stacked
                                  className="justify-center transition-colors duration-200"
                                />
                              </div>
                              <p
                                className={`mt-0.5 text-[8px] sm:text-[9px] leading-snug font-normal line-clamp-2 text-center transition-colors duration-200 ${
                                  isActive
                                    ? "text-amber-700/80"
                                    : "text-gray-600 group-hover/cell:text-gray-700"
                                }`}
                              >
                                {caption.body}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="px-3 sm:px-4 pb-1" aria-hidden="true" />
                    </div>

                    <div className="w-full sm:w-1/2 self-start bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                      <div className="px-3 sm:px-4 pt-3 pb-2">
                        <div className="flex flex-wrap items-center gap-2 mb-2 px-1">
                          <FlashFreeGiftBadge />
                          <span className="text-[11px] sm:text-xs font-bold leading-tight text-emerald-700">
                            {FREE_GIFT_ORDER_MESSAGE}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-x-2 gap-y-3 bg-white">
                          {globalFreeGiftRowCells.map((cell, index) => {
                            const product = products.find(
                              (entry) => entry.id === cell.productId,
                            );
                            if (!product) return null;

                            const isActive = activeFreeGiftCellIndex === index;
                            const isThumbnailFavorited = isFavorite(product.id);
                            const cellPosterSrc = cell.src;
                            const cellWebp480 = webpVariant(cellPosterSrc, 480);
                            const cellWebp960 = webpVariant(cellPosterSrc, 960);
                            const cellWebpFull = webpVariant(cellPosterSrc);
                            const cellWebpSrcSet = cellWebp960
                              ? `${cellWebp480 ?? cellWebp960} 480w, ${cellWebp960} 960w${
                                  cellWebpFull ? `, ${cellWebpFull} 1600w` : ""
                                }`
                              : "";

                            return (
                              <div
                                key={`free-gift-${index}`}
                                role="button"
                                tabIndex={0}
                                onMouseEnter={() => setActiveFreeGiftCellIndex(index)}
                                onFocus={() => setActiveFreeGiftCellIndex(index)}
                                onKeyDown={(e) => {
                                  if (e.key !== "Enter" && e.key !== " ") return;
                                  e.preventDefault();
                                  setActiveFreeGiftCellIndex(index);
                                  openProductDetails(cell.productId);
                                }}
                                onClick={() => {
                                  setActiveFreeGiftCellIndex(index);
                                  openProductDetails(cell.productId);
                                }}
                                aria-label={`View ${cell.title} free gift offer`}
                                className="group/cell flex flex-col items-stretch text-left cursor-pointer"
                              >
                                <div
                                  className={`relative aspect-square w-full overflow-hidden rounded-md border-2 transition-all duration-200 ${
                                    isActive
                                      ? "border-amber-400 ring-2 ring-amber-400/30"
                                      : "border-gray-200 group-hover/cell:border-amber-300"
                                  }`}
                                >
                                  <picture className="absolute inset-0 block h-full w-full">
                                    {cellWebp960 && (
                                      <source
                                        type="image/webp"
                                        srcSet={cellWebpSrcSet}
                                        sizes="(min-width: 1024px) 160px, (min-width: 640px) 25vw, 50vw"
                                      />
                                    )}
                                    <img
                                      src={cellPosterSrc}
                                      alt=""
                                      loading="lazy"
                                      decoding="async"
                                      className="block h-full w-full object-cover"
                                    />
                                  </picture>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFavorite(product);
                                    }}
                                    aria-label={
                                      isThumbnailFavorited
                                        ? `Remove ${product.name} from wishlist`
                                        : `Save ${product.name} to wishlist`
                                    }
                                    title={
                                      isThumbnailFavorited
                                        ? "Remove from wishlist"
                                        : "Save to wishlist"
                                    }
                                    className={`absolute top-1 right-1 z-10 flex h-4 w-4 items-center justify-center rounded-full border shadow-sm transition-all duration-200 ${
                                      isThumbnailFavorited
                                        ? "border-red-200 bg-white text-red-500"
                                        : "border-white/70 bg-white/90 text-gray-500 hover:text-red-500"
                                    }`}
                                  >
                                    <svg
                                      className="h-2.5 w-2.5"
                                      fill={
                                        isThumbnailFavorited ? "currentColor" : "none"
                                      }
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z"
                                      />
                                    </svg>
                                  </button>
                                </div>
                                <p
                                  className={`mt-1.5 text-[10px] sm:text-[11px] leading-tight font-semibold line-clamp-1 text-center transition-colors duration-200 ${
                                    isActive
                                      ? "text-amber-600"
                                      : "text-gray-900 group-hover/cell:text-gray-900"
                                  }`}
                                >
                                  {cell.title}
                                </p>
                                <div className="mt-0.5 flex justify-center">
                                  <ProductPrice
                                    product={product}
                                    size="xs"
                                    stacked
                                    className={`justify-center transition-colors duration-200 ${
                                      isActive ? "text-gray-950" : "text-gray-800"
                                    }`}
                                  />
                                </div>
                                <p
                                  className={`mt-0.5 text-[8px] sm:text-[9px] leading-snug font-normal line-clamp-2 text-center transition-colors duration-200 ${
                                    isActive
                                      ? "text-amber-700/80"
                                      : "text-gray-600 group-hover/cell:text-gray-700"
                                  }`}
                                >
                                  {cell.body}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="px-3 sm:px-4 pb-1" aria-hidden="true" />
                    </div>
                  </div>
                )}
              </div>

              {/* Preview pane: far-right column, sticky on desktop. The inner card scrolls independently so the page stays still while the cursor is over it. */}
              <aside
                ref={previewPaneRef}
                className="hidden lg:block lg:sticky lg:self-start w-full bg-slate-900"
                style={{ top: "7rem" }}
              >
                <div
                  className="rounded-2xl bg-white border-2 border-amber-300 shadow-2xl overflow-x-hidden overflow-y-auto overscroll-y-auto scroll-smooth"
                  style={{
                    maxHeight: desktopPreviewHeight
                      ? `${desktopPreviewHeight}px`
                      : "calc(100dvh - 8rem)",
                  }}
                >
                  {previewCardBody}
                </div>
              </aside>

              {/* Mobile preview overlay: floats in front of the page so the
                  user can interact with the preview. The background page is
                  only lightly blurred (not covered) so context stays visible.
                  Desktop (lg+) keeps using the sticky aside above. */}
              {isMobilePreviewOpen && preview && (
                <div
                  className="lg:hidden fixed inset-0 z-[60] overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-4 sm:py-6 bg-black/15 backdrop-blur-[3px]"
                  style={{ WebkitOverflowScrolling: "touch" }}
                  role="dialog"
                  aria-modal="true"
                  aria-label={`Preview: ${preview.product.name}`}
                  onClick={closeMobilePreview}
                >
                  <div
                    className="relative w-full max-w-md min-h-min mx-auto bg-white shadow-2xl overflow-x-hidden rounded-2xl border-2 border-amber-300"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={closeMobilePreview}
                      aria-label="Close preview"
                      className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-800 shadow-md ring-1 ring-black/10 hover:bg-white active:scale-95 transition-all duration-150"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                    {previewCardBody}
                  </div>
                </div>
              )}


            </div>
          </div>
        </section>

        {/* Features Section - Refined Typography */}
        <section className="py-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(251,191,36,0.03)_0%,transparent_50%)]"></div>
          <div className="relative container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-5 backdrop-blur-sm bg-white/5 border border-gray-800/50 rounded-xl hover:bg-amber-400/5 hover:border-amber-400/20 transition-all duration-300">
                <div className="relative w-12 h-12 mx-auto mb-4">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-amber-600/20 rounded-full blur-lg"></div>
                  <div className="relative w-full h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-100 mb-2">
                  Authenticity Guaranteed
                </h3>
                <p className="text-gray-400 font-light text-xs leading-relaxed">
                  Every item is verified for authenticity by our expert team
                </p>
              </div>
              <div className="p-5 backdrop-blur-sm bg-white/5 border border-gray-800/50 rounded-xl hover:bg-amber-400/5 hover:border-amber-400/20 transition-all duration-300">
                <div className="relative w-12 h-12 mx-auto mb-4">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-amber-600/20 rounded-full blur-lg"></div>
                  <div className="relative w-full h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-100 mb-2">
                  Free Worldwide Shipping
                </h3>
                <p className="text-gray-400 font-light text-xs leading-relaxed">
                  Complimentary shipping on all orders over $500
                </p>
              </div>
              <div className="p-5 backdrop-blur-sm bg-white/5 border border-gray-800/50 rounded-xl hover:bg-amber-400/5 hover:border-amber-400/20 transition-all duration-300">
                <div className="relative w-12 h-12 mx-auto mb-4">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-amber-600/20 rounded-full blur-lg"></div>
                  <div className="relative w-full h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v13m0-13V6a2 2 0 112 0v1m-2 0V6a2 2 0 00-2 0v1m2 0V9.5a2.5 2.5 0 00-5 0v0A2.5 2.5 0 007.5 12H9"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-100 mb-2">
                  Concierge Service
                </h3>
                <p className="text-gray-400 font-light text-xs leading-relaxed">
                  Personal shopping assistance from luxury experts
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 relative">
          <div className="relative container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-light uppercase tracking-[0.2em] text-white">
                  Shopping with more clarity builds more trust.
                </h2>
                <p className="mt-4 text-sm font-light text-gray-300 max-w-3xl mx-auto leading-relaxed">
                  Strong product pages and clear support information reduce
                  hesitation. That matters for both search traffic and buyers
                  discovering your brand for the first time.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-100">
                    Dedicated collections
                  </h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-gray-300">
                    Visitors can browse focused category pages instead of
                    hunting through one long homepage.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-100">
                    Visible support content
                  </h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-gray-300">
                    Shipping, returns and sizing information now have real
                    destinations that support conversion.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-100">
                    Lead capture without login
                  </h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-gray-300">
                    Newsletter signup is open to guests so you can actually
                    collect interest before account creation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 relative">
          <div className="relative container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-light uppercase tracking-[0.2em] text-white">
                Frequently asked questions
              </h2>
              <p className="mt-4 text-sm font-light text-gray-300 leading-relaxed">
                Clear answers help first-time buyers move faster.
              </p>
            </div>

            <div className="space-y-4">
              {faqItems.map((item) => (
                <div
                  key={item.question}
                  className="rounded-2xl border border-white/10 bg-white/5 p-6"
                >
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-100">
                    {item.question}
                  </h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-gray-300">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
